/**
 * In-app debug logging for Word add-ins (no DevTools in task pane).
 * Buffers log lines in memory; user copies to clipboard and pastes into email.
 */

/* global Office */

import { isDebugLogPanelVisible, setDebugLogPanelVisible } from './addInPreferences';

export type DebugLogLevel = 'log' | 'info' | 'warn' | 'error';

export interface DebugLogEntry {
  ts: string;
  level: DebugLogLevel;
  message: string;
  data?: unknown;
}

const STORAGE_ENABLED_KEY = 'courtyard.remoteDebug';
const SESSION_ID_KEY = 'courtyard.remoteDebug.sessionId';
const MAX_ENTRIES = 2500;

let enabled = false;
let sessionId = '';
let entries: DebugLogEntry[] = [];
let consoleHookInstalled = false;
let suppressConsoleHook = false;
let listeners: Array<() => void> = [];

function notifyListeners(): void {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      // ignore
    }
  });
}

function safeSerialize(value: unknown): unknown {
  if (value === undefined) return undefined;
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  try {
    JSON.stringify(value);
    return value;
  } catch {
    return String(value);
  }
}

function normalizeArgs(args: unknown[]): unknown {
  if (!args.length) return undefined;
  if (args.length === 1) return safeSerialize(args[0]);
  return args.map(safeSerialize);
}

function ensureSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) {
      sessionId = existing;
      return sessionId;
    }
  } catch {
    // ignore
  }
  sessionId = `wd-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  try {
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  } catch {
    // ignore
  }
  return sessionId;
}

function readEnabledFromUrl(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('remoteDebug') === '1';
  } catch {
    return false;
  }
}

function trimEntries(): void {
  if (entries.length > MAX_ENTRIES) {
    entries = entries.slice(-MAX_ENTRIES);
  }
}

function clientEnvironment(): Record<string, string> {
  return {
    sessionId: ensureSessionId(),
    platform: typeof Office !== 'undefined' ? String(Office.context?.platform ?? 'unknown') : 'browser',
    host: typeof Office !== 'undefined' ? String(Office.context?.host ?? 'unknown') : 'unknown',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    href: typeof window !== 'undefined' ? window.location.href : '',
  };
}

export function isRemoteDebugEnabled(): boolean {
  return enabled;
}

export function getRemoteDebugSessionId(): string {
  return ensureSessionId();
}

export function getRemoteDebugBufferedCount(): number {
  return entries.length;
}

export function getDebugLogEntries(): DebugLogEntry[] {
  return [...entries];
}

export function subscribeDebugLog(onChange: () => void): () => void {
  listeners.push(onChange);
  return () => {
    listeners = listeners.filter((cb) => cb !== onChange);
  };
}

export function setRemoteDebugEnabled(next: boolean): void {
  enabled = next;
  try {
    if (next) {
      localStorage.setItem(STORAGE_ENABLED_KEY, '1');
    } else {
      localStorage.removeItem(STORAGE_ENABLED_KEY);
    }
  } catch {
    // ignore
  }
  if (next) {
    pushEntry('info', 'Debug logging enabled', [clientEnvironment()]);
  } else {
    pushEntry('info', 'Debug logging disabled', []);
  }
  notifyListeners();
}

function pushEntry(level: DebugLogLevel, message: string, args?: unknown[]): void {
  if (!enabled) return;
  ensureSessionId();
  entries.push({
    ts: new Date().toISOString(),
    level,
    message,
    data: args ? normalizeArgs(args) : undefined,
  });
  trimEntries();
  notifyListeners();
}

/** Structured debug line (paragraph sync, submit, undo, etc.). */
export function debugLog(message: string, data?: Record<string, unknown>): void {
  pushEntry('log', message, data ? [data] : undefined);
}

export function debugWarn(message: string, data?: Record<string, unknown>): void {
  pushEntry('warn', message, data ? [data] : undefined);
}

export function debugError(message: string, data?: Record<string, unknown>): void {
  pushEntry('error', message, data ? [data] : undefined);
}

export function recordRemoteDebug(
  level: DebugLogLevel,
  message: string,
  args?: unknown[],
): void {
  if (!enabled) return;
  suppressConsoleHook = true;
  pushEntry(level, message, args);
  suppressConsoleHook = false;
}

function installConsoleHook(): void {
  if (consoleHookInstalled || typeof window === 'undefined') return;
  consoleHookInstalled = true;

  const wrap =
    (level: DebugLogLevel, original: (...params: unknown[]) => void) =>
    (...params: unknown[]) => {
      original.apply(console, params);
      if (!enabled || suppressConsoleHook) return;
      const message =
        params.length === 0
          ? ''
          : typeof params[0] === 'string'
            ? params[0]
            : String(params[0]);
      const rest = params.length <= 1 ? [] : params.slice(1);
      recordRemoteDebug(level, message, rest);
    };

  console.log = wrap('log', console.log.bind(console));
  console.info = wrap('info', console.info.bind(console));
  console.warn = wrap('warn', console.warn.bind(console));
  console.error = wrap('error', console.error.bind(console));
}

export function initRemoteDebugLog(): void {
  if (typeof window === 'undefined') return;
  ensureSessionId();
  if (readEnabledFromUrl()) {
    setDebugLogPanelVisible(true);
    enabled = true;
  } else {
    enabled = isDebugLogPanelVisible();
  }
  installConsoleHook();
  if (enabled) {
    pushEntry('info', 'Debug logging initialized', [clientEnvironment()]);
  }
}

export function clearDebugLog(): void {
  entries = [];
  notifyListeners();
}

function formatEntryLine(entry: DebugLogEntry): string {
  const dataStr = entry.data === undefined ? '' : ` ${JSON.stringify(entry.data)}`;
  return `${entry.ts} [${entry.level}] ${entry.message}${dataStr}`;
}

export function exportDebugLogText(): string {
  const header = [
    'Courtyard Word Add-in Debug Log',
    `Generated: ${new Date().toISOString()}`,
    JSON.stringify(clientEnvironment()),
    `Entry count: ${entries.length}`,
    '---',
  ].join('\n');
  return `${header}\n${entries.map(formatEntryLine).join('\n')}\n`;
}

export type CopyDebugLogResult = {
  ok: boolean;
  error?: string;
  charCount?: number;
};

/** Copy full debug log to system clipboard (works in Word task pane; download often blocked). */
export async function copyDebugLogToClipboard(): Promise<CopyDebugLogResult> {
  if (typeof document === 'undefined') {
    return { ok: false, error: 'Clipboard is not available in this environment.' };
  }

  const text = exportDebugLogText();
  if (entries.length === 0) {
    return { ok: false, error: 'No log entries yet. Reproduce the issue first.' };
  }

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      pushEntry('info', 'Debug log copied to clipboard', [{ entryCount: entries.length, charCount: text.length }]);
      return { ok: true, charCount: text.length };
    }
  } catch {
    // Fall through to execCommand — common in Office iframe hosts.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'true');
    textarea.style.position = 'fixed';
    textarea.style.top = '0';
    textarea.style.left = '0';
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    if (copied) {
      pushEntry('info', 'Debug log copied to clipboard (fallback)', [{ entryCount: entries.length, charCount: text.length }]);
      return { ok: true, charCount: text.length };
    }
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Could not copy to clipboard.',
    };
  }

  return { ok: false, error: 'Clipboard access was denied. Try selecting the log text manually.' };
}

/** @deprecated Download is blocked in many Word hosts; use copyDebugLogToClipboard. */
export function downloadDebugLogFile(): void {
  void copyDebugLogToClipboard();
}

/** @deprecated use exportDebugLogText */
export function exportBufferedDebugLogText(): string {
  return exportDebugLogText();
}

/** Back-compat aliases used during migration */
export const isDebugLogEnabled = isRemoteDebugEnabled;
export const setDebugLogEnabled = setRemoteDebugEnabled;
